/**
 * Cart drawer - add to cart flows.
 * 1. Theme-owned: one document-level submit listener for /cart/add forms.
 * 2. App-owned (rwbzone-gear personalize app, which hardcodes a redirect to /cart):
 *    a one-shot ticket armed by the app's successful POST, used to cancel that redirect
 *    through the Navigation API and open the drawer instead.
 * Requires cart-drawer-core.js and cart-drawer.js.
 */
(function () {
  'use strict';

  var Core = window.CartDrawerCore;
  var Drawer = window.CartDrawer;
  if (!Core || !Drawer || window.__cartDrawerAtcLoaded) return;
  window.__cartDrawerAtcLoaded = true;

  var ADD_ACTION = /\/cart\/add\/?$/;
  var TICKET_TTL = 3000;

  // ---------------------------------------------------------------- helpers

  function formPath(form) {
    try {
      return new URL(form.getAttribute('action') || '', window.location.href).pathname;
    } catch (error) {
      return '';
    }
  }

  function addUrl() {
    return Drawer.root() + 'cart/add.js';
  }

  function post(options) {
    options.method = 'POST';
    options.credentials = 'same-origin';
    return Drawer.request(addUrl(), options).then(Drawer.readResult, function () {
      return { outcome: 'unknown', json: null };
    });
  }

  // Read what the customer chose at the moment they clicked: the request may wait behind
  // other cart operations, and the form can change in the meantime.
  function snapshotMain(form) {
    var body = new FormData(form);
    body.append('sections', Drawer.SECTION);
    body.append('sections_url', window.location.pathname);
    return body;
  }

  function postMain(body) {
    return post({ headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: body });
  }

  function postAddons(ids) {
    return post({
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: ids.map(function (id) { return { id: id, quantity: 1 }; }),
        sections: Drawer.SECTION,
        sections_url: window.location.pathname,
      }),
      // survives the page leaving for /cart when the redirect could not be cancelled
      keepalive: true,
    });
  }

  function checkedAddons(scope) {
    var ids = [];
    Array.prototype.forEach.call(scope.querySelectorAll('.gift-box .upsell-checkbox:checked'), function (box) {
      var id = parseInt(box.getAttribute('data-variant-id'), 10);
      if (!isNaN(id) && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  function clearFormError(form) {
    var next = form.nextElementSibling;
    if (next && next.classList.contains('cart-drawer-form-error')) next.remove();
  }

  function showFormError(form, message) {
    clearFormError(form);
    var note = document.createElement('p');
    note.className = 'warning cart-drawer-form-error';
    note.setAttribute('role', 'alert');
    note.textContent = message;
    form.insertAdjacentElement('afterend', note);
  }

  function closeFancybox() {
    var jq = window.jQuery;
    if (jq && jq.fancybox && document.body.classList.contains('fancybox-active')) jq.fancybox.close();
  }

  function openFresh(el, trigger) {
    return el.refreshAndOpen(trigger).then(function (ok) {
      if (!ok) Drawer.goToCart();
    });
  }

  // Render html when it is the newest state we have; otherwise ask the server.
  function openWith(el, html, trigger) {
    closeFancybox();
    if (Drawer.queue.pending === 0 && el.render(html)) {
      el.open(trigger);
      return Promise.resolve();
    }
    return openFresh(el, trigger);
  }

  // ---------------------------------------------------- theme-owned submits

  // The personalize app does not guard against a second click while it is adding.
  var appBusy = false;
  var appClickLock = false;
  var appClickTimer = null;
  // From the moment the app's add succeeded until the drawer is open (or we gave up): the app
  // reports "done" long before that when gifts are still being added, and the customer must
  // not be able to add the product a second time in between.
  var appTxn = false;
  var appTxnTimer = null;

  function beginAppTxn() {
    appTxn = true;
    window.clearTimeout(appTxnTimer);
    appTxnTimer = window.setTimeout(endAppTxn, 15000);
  }

  function endAppTxn() {
    appTxn = false;
    window.clearTimeout(appTxnTimer);
  }

  function releaseAppClickLock() {
    appClickLock = false;
    window.clearTimeout(appClickTimer);
  }

  window.addEventListener('sl-adding-to-cart', function () { appBusy = true; });
  window.addEventListener('sl-added-to-cart', function () {
    appBusy = false;
    releaseAppClickLock();
  });

  // The app's click handler has no busy check, and the page stays open after a successful add
  // in drawer mode, so a second click would add the product again. Swallow it in the capture
  // phase, before the app's own listener. Only the button inside the product form takes the
  // lock: the preview-modal buttons just ask that button to add (sl-trigger-atc).
  document.addEventListener('click', function (event) {
    var button = event.target && event.target.closest ? event.target.closest('.sl-atc-btn') : null;
    if (!button) return;
    if (appBusy || appClickLock || appTxn || button.classList.contains('running')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    var buttonForm = button.closest('form');
    if (buttonForm) {
      clearFormError(buttonForm);
      appClickLock = true;
      window.clearTimeout(appClickTimer);
      // safety: never leave the button dead if the app never reports back
      appClickTimer = window.setTimeout(releaseAppClickLock, 20000);
    }
  }, true);

  function addFromForm(form, el, button, body, addons) {
    return Drawer.queue.enqueue(function () { return postMain(body); }).then(function (main) {
      if (main.outcome === 'rejected') {
        var message = (main.json && (main.json.description || main.json.message)) || el.dataset.errorText || '';
        showFormError(form, message);
        return undefined;
      }
      if (main.outcome === 'unknown') {
        // The product may be in the cart: show the real cart, never ask for a retry.
        closeFancybox();
        return openFresh(el, button);
      }

      if (!addons.length) return openWith(el, Drawer.extractSection(main.json), button);

      return Drawer.queue.enqueue(function () { return postAddons(addons); }).then(function (addon) {
        if (addon.outcome === 'ok') return openWith(el, Drawer.extractSection(addon.json), button);
        if (addon.outcome === 'rejected') {
          console.error('Cart drawer: add-on was not added', addon.json);
          return openWith(el, Drawer.extractSection(main.json), button);
        }
        closeFancybox();
        return openFresh(el, button);
      });
    });
  }

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!ADD_ACTION.test(formPath(form))) return;
    // The app's hold script, or any other handler, already took this submit.
    if (event.defaultPrevented) return;
    var el = Drawer.getDrawer();
    if (!el) return;

    // The app hides the real submit button and listens to clicks on its own button only.
    // A submit that still gets here (Enter in a field) must go through the app, or the
    // product would be added without its personalization.
    if (form.querySelector('.sl-atc-btn')) {
      event.preventDefault();
      // "running" is the app's own in-flight marker on its button; it also covers an add that
      // started before this deferred script was loaded.
      var appRunning = appBusy || appClickLock || appTxn || Boolean(form.querySelector('.sl-atc-btn.running'));
      if (!appRunning) window.dispatchEvent(new CustomEvent('sl-trigger-atc'));
      return;
    }

    event.preventDefault();
    if (form.dataset.cartDrawerAdding === 'true') return;
    form.dataset.cartDrawerAdding = 'true';
    var button = event.submitter || form.querySelector('[name="add"]');
    if (button) {
      button.classList.add('is-adding');
      button.setAttribute('aria-busy', 'true');
    }
    clearFormError(form);
    var body = snapshotMain(form);
    var addons = checkedAddons(form);

    var done = function () {
      delete form.dataset.cartDrawerAdding;
      if (button) {
        button.classList.remove('is-adding');
        button.removeAttribute('aria-busy');
      }
    };
    addFromForm(form, el, button, body, addons).then(done, function (error) {
      console.error('Cart drawer: add to cart failed', error);
      done();
    });
  });

  // ------------------------------------------------------ app-owned submits

  var nav = window.navigation;
  var originalFetch = window.fetch;
  if (!nav || typeof nav.addEventListener !== 'function' || typeof originalFetch !== 'function') return;
  if (!Drawer.getDrawer()) return;

  var ticket = Core.createTicket(TICKET_TTL);

  // From here on the drawer adds the gift boxes for personalized products, after the main
  // product succeeded (assets/product-page.js.liquid then leaves them alone). Without this
  // path (no Navigation API) the old order stays: gifts first, then the app adds the product.
  Drawer.ownsAppAddons = true;

  function addAppGifts(ids) {
    Drawer.queue.enqueue(function () { return postAddons(ids); }).then(function (result) {
      if (result.outcome !== 'ok') console.error('Cart drawer: gift box was not added', result.json);
    });
  }

  // The app only logs a refused add. Say why, under the product form.
  function explainAppFailure(response) {
    if (!response || typeof response.clone !== 'function') return;
    if (!(response.status >= 400 && response.status < 500)) return;
    response.clone().json().then(function (json) {
      var message = json && (json.description || json.message);
      var button = document.querySelector('form .sl-atc-btn');
      var form = button && button.closest('form');
      if (message && form) showFormError(form, message);
    }, function () {});
  }

  // Observe only: return the very same promise and never read the response body.
  window.fetch = function (input, init) {
    var promise = originalFetch.apply(window, arguments);
    var watched = false;
    try {
      watched = Core.isAppAddRequest(input, init, window.location.href);
    } catch (error) {
      watched = false;
    }
    if (watched) {
      // what the customer ticked when the app sent the product, not what the page shows later
      var gifts = checkedAddons(document);
      promise.then(
        function (response) {
          if (response && response.ok) {
            ticket.arm(Date.now());
            beginAppTxn();
            // queued before the redirect we are about to cancel, so the refresh runs after it
            if (gifts.length) addAppGifts(gifts);
          } else {
            explainAppFailure(response);
          }
        },
        function () {}
      );
    }
    return promise;
  };

  nav.addEventListener('navigate', function (event) {
    var paths = Core.cartPaths(Drawer.root());
    if (!Core.shouldInterceptCartNavigation(event, ticket, paths, Date.now(), window.location.origin)) return;
    var el = Drawer.getDrawer();
    if (!el) return;
    ticket.consume();
    event.preventDefault();
    // The app closes its own modals only by leaving the page. We cancelled that, so ask it to
    // (these are the app's own events; the canvas one also restores body scrolling).
    window.dispatchEvent(new CustomEvent('sl-hide-canvas-modal'));
    window.dispatchEvent(new CustomEvent('sl-hide-preview-modal'));
    openFresh(el, null).then(endAppTxn, endAppTxn);
  });
})();
